import React, { useState, useEffect, useContext } from "react";
import { FaPlusCircle } from "react-icons/fa";
import { DropdownButton, Dropdown } from "react-bootstrap";
import SingleKit from "./SingleKit";
import SingleOrder from "./SingleOrder";
import ApiService from "../../../../ApiService";
import { PAGES } from "../../../SuperAdminPanel/Dashboard";
import { getUserRoles, ROLE_NAME } from "../../../../util/authUtil";
import { SnackbarContext } from "../../../AlertSystem";

const KITS = {
  ALL: "All Orders",
  ACTIVE: "Active Kits",
  PREVIOUS: "Previous Kits"
};

function separateKitsByStatus(kits) {
  const completeKits = kits.filter(function(kit) {
    return kit.kitStatus === "COMPLETE" || kit.kitStatus === "REPORT_READY";
  });

  const otherKits = kits.filter(function(kit) {
    return kit.kitStatus !== "COMPLETE";
  });

  return {
    completeKits: completeKits,
    otherKits: otherKits
  };
}
function formatDate(inputDate) {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  const date = new Date(inputDate);
  const day = date.getDate();
  const month = months[date.getMonth()];
  const year = date
    .getFullYear()
    .toString()
    .substr(-2);

  return `${day} ${month} '${year}`;
}

export default function KitDetails({ patientId, patientEmail }) {
  const [activeTab, setActiveTab] = useState(KITS.ALL);
  const [completedKits, setCompletedKits] = useState([]);
  const [otherKits, setOtherKits] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const { showSnackbar } = useContext(SnackbarContext);

  useEffect(() => {
    if (!patientId && !patientEmail) return;

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [kitsResponse, roles, ordersResponse] = await Promise.all([
          patientId ? ApiService.getKitsOfPatientByPatientId(patientId) : Promise.resolve({ data: [] }),
          getUserRoles(),
          patientEmail ? ApiService.getClinicianOrderSummary(patientEmail) : Promise.resolve({ data: [] })
        ]);

        const { completeKits, otherKits } = separateKitsByStatus(kitsResponse?.data ?? []);
        setCompletedKits(completeKits);
        setOtherKits(otherKits);
        // Sort allOrders by createdAt descending before setting
        const orders = Array.isArray(ordersResponse?.data) ? ordersResponse.data : [];
        const sortedOrders = orders.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setAllOrders(sortedOrders);
        setIsSuperAdmin(Array.isArray(roles) && roles.includes(ROLE_NAME.SUPER_ADMIN));
      } catch (error) {
        showSnackbar({ message: "Error fetching kits or orders. Please try again later.", severity: "error" });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [patientId, patientEmail, showSnackbar]);

  const orderNewKit = goTo => {
    if (!patientId) return;
    const query = new URLSearchParams({
      patientId: patientId,
      page: goTo
    }).toString();
    window.open(`/super-admin?${query}`, "_blank");
  };

  if (isLoading) {
    return (
      <div className="dashboardContent">
        <div className="tabsBackground">
          <button className="contentButton contentButtonActive">Loading...</button>
        </div>
        <div className="tabsContent px-4">
          <p className="loading"></p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboardContent">
      <div className="tabsBackground">
        <button onClick={() => setActiveTab(KITS.ALL)} className={`contentButton ${activeTab === KITS.ALL && "contentButtonActive"}`}>
          {KITS.ALL}
        </button>
        <button onClick={() => setActiveTab(KITS.ACTIVE)} className={`contentButton ${activeTab === KITS.ACTIVE && "contentButtonActive"}`}>
          {KITS.ACTIVE}
        </button>
        <button onClick={() => setActiveTab(KITS.PREVIOUS)} className={`contentButton ${activeTab === KITS.PREVIOUS && "contentButtonActive"}`}>
          {KITS.PREVIOUS}
        </button>
      </div>
      {isSuperAdmin && !isLoading && (
        <DropdownButton
          id="dropdown-basic-button"
          title="Order a new kit"
          variant="primary"
          className="ml-3"
          size="md"
          style={{
            color: "white",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            justifySelf: "flex-end"
          }}
        >
          <Dropdown.Item onClick={() => orderNewKit(PAGES.CREATE_ORDER)}>
            <FaPlusCircle />
            &nbsp;&nbsp; Order a kit
          </Dropdown.Item>
          <Dropdown.Item onClick={() => orderNewKit(PAGES.CREATE_PREP_ORDER)}>
            <FaPlusCircle />
            &nbsp;&nbsp; Order a PrEP kit
          </Dropdown.Item>
          <Dropdown.Item onClick={() => orderNewKit(PAGES.UPLOAD_RESULTS)}>
            <FaPlusCircle />
            &nbsp;&nbsp; Upload Results
          </Dropdown.Item>
        </DropdownButton>
      )}

      {activeTab === KITS.PREVIOUS ? (
        completedKits.length > 0 ? (
          <div className="tabsContent px-4">
            {completedKits.map((kit, index) => (
              <SingleKit
                key={index}
                kitStatus={kit.kitStatus}
                kitId={kit.kitId}
                isClinicalRequired={kit.clinicianId ? false : true}
                dateOfRegistration={formatDate(kit.registrationDateTime)}
                kitDetails={kit}
                isSuperAdmin={isSuperAdmin ?? false}
              />
            ))}
          </div>
        ) : (
          <div className="tabsContent px-4">
            {isLoading ? <p className="loading"></p> : <h1 className="noRecordFound">No Previous kits found</h1>}
          </div>
        )
      ) : activeTab === KITS.ACTIVE ? (
        otherKits.length > 0 ? (
          <div className="tabsContent px-4">
            {otherKits.map((kit, index) => (
              <SingleKit
                key={index}
                kitStatus={kit.kitStatus}
                kitId={kit.kitId}
                isClinicalRequired={kit.clinicianId ? false : true}
                dateOfRegistration={formatDate(kit.registrationDateTime)}
                kitDetails={kit}
                isSuperAdmin={isSuperAdmin ?? false}
              />
            ))}
          </div>
        ) : (
          <div className="tabsContent px-4">{isLoading ? <p className="loading"></p> : <h1 className="noRecordFound">No Active kits found</h1>}</div>
        )
      ) : activeTab === KITS.ALL ? (
        allOrders.length > 0 ? (
          <div className="tabsContent px-4">
            {allOrders.map((order, index) => (
              <SingleOrder key={order?.id || index} order={order} />
            ))}
          </div>
        ) : (
          <div className="tabsContent px-4">{isLoading ? <p className="loading"></p> : <h1 className="noRecordFound">No orders found</h1>}</div>
        )
      ) : null}

      {/* {activeTab === KITS.PREVIOUS && (
        <div className="tabsContent px-4">
          <SingleKit />
          <SingleKit />
        </div>
      )} */}
    </div>
  );
}
